import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import { jsPDF } from 'jspdf';
import RegistrarPagoModal from './components/RegistrarPagoModal';
import DetallePagoModal from './components/DetallePagoModal';
import AdminLayout from '../../components/layout/AdminLayout';

interface Pago {
  id: string;
  usuario_id: string;
  monto: number;
  metodo_pago: string;
  concepto: string;
  fecha_pago: string;
  estado: string;
  sucursal_id: string;
  created_at: string;
  banco?: string | null;
  numero_comprobante?: string | null;
  notas?: string | null;
  usuario?: {
    nombre: string;
    email: string;
    telefono: string;
    tipo_membresia: string;
  };
}

interface MetodoPagoOption {
  value: string;
  label: string;
}

const METODOS_PAGO_POR_DEFECTO: MetodoPagoOption[] = [];

const normalizarMetodoPago = (metodo: string) =>
  (metodo || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const construirMetodosPagoDesdeConfig = (cfg: any): MetodoPagoOption[] => {
  const permiteEfectivo = cfg?.efectivo ?? true;
  const permiteTransferencia = cfg?.transferencia ?? true;
  const permiteTarjeta = (cfg?.tarjeta_credito ?? true) || (cfg?.tarjeta_debito ?? true);
  const permiteOtro = cfg?.otro ?? true;

  const metodos: MetodoPagoOption[] = [];
  if (permiteEfectivo) metodos.push({ value: 'efectivo', label: 'Efectivo' });
  if (permiteTarjeta) metodos.push({ value: 'tarjeta', label: 'Tarjeta' });
  if (permiteTransferencia) {
    metodos.push({ value: 'transferencia', label: 'Transferencia' });
    metodos.push({ value: 'deposito', label: 'Deposito' });
    metodos.push({ value: 'cheque', label: 'Cheque' });
  }
  if (permiteOtro) {
    metodos.push({ value: 'otro', label: 'Otro' });
  }

  return metodos;
};

export default function PagosPage() {
  const { sucursalActual } = useSucursal();
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroMetodo, setFiltroMetodo] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [mostrarModalRegistrar, setMostrarModalRegistrar] = useState(false);
  const [pagoSeleccionado, setPagoSeleccionado] = useState<Pago | null>(null);
  const [metodosPagoManual, setMetodosPagoManual] = useState<MetodoPagoOption[]>(METODOS_PAGO_POR_DEFECTO);

  // Cargar pagos desde Supabase
  useEffect(() => {
    cargarPagos();
    cargarMetodosPago();
  }, [sucursalActual]);

  const cargarPagos = async () => {
    if (!sucursalActual?.id) {
      setPagos([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const query = supabase
        .from('pagos')
        .select(`
          *,
          usuario:usuarios!pagos_usuario_id_fkey (
            nombre,
            email,
            telefono,
            tipo_membresia
          )
        `)
        .eq('sucursal_id', sucursalActual.id)
        .order('fecha_pago', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      setPagos(data || []);
    } catch (error) {
      console.error('Error al cargar pagos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarMetodosPago = async () => {
    if (!sucursalActual?.id) {
      setMetodosPagoManual(METODOS_PAGO_POR_DEFECTO);
      return;
    }

    try {
      const gimnasioId = (sucursalActual as any)?.gimnasio_id;
      if (!gimnasioId) {
        setMetodosPagoManual(METODOS_PAGO_POR_DEFECTO);
        return;
      }

      const { data: cfgSucursalRows, error: errorSucursal } = await supabase
        .from('pago_configuracion')
        .select('*')
        .eq('gimnasio_id', gimnasioId)
        .eq('sucursal_id', sucursalActual.id)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (errorSucursal) throw errorSucursal;

      let cfg = cfgSucursalRows?.[0];
      if (!cfg) {
        const { data: cfgGymRows, error: errorGym } = await supabase
          .from('pago_configuracion')
          .select('*')
          .eq('gimnasio_id', gimnasioId)
          .is('sucursal_id', null)
          .order('updated_at', { ascending: false })
          .limit(1);

        if (errorGym) throw errorGym;
        cfg = cfgGymRows?.[0];
      }

      const metodos = construirMetodosPagoDesdeConfig(cfg);
      setMetodosPagoManual(metodos);

      if (filtroMetodo !== 'todos' && !metodos.some((m) => m.value === filtroMetodo)) {
        setFiltroMetodo('todos');
      }
    } catch (error) {
      console.error('Error al cargar metodos de pago:', error);
      setMetodosPagoManual(METODOS_PAGO_POR_DEFECTO);
    }
  };

  const handleOpenRegistrarModal = async () => {
    await cargarMetodosPago();
    setMostrarModalRegistrar(true);
  };

  const obtenerClaveFecha = (fecha: string): string | null => {
    if (!fecha) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      return fecha;
    }

    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return null;

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Filtrar pagos
  const pagosFiltrados = pagos.filter(pago => {
    const cumpleFiltroEstado = filtroEstado === 'todos' || pago.estado === filtroEstado;
    const cumpleFiltroMetodo =
      filtroMetodo === 'todos' || normalizarMetodoPago(pago.metodo_pago) === filtroMetodo;
    const claveFechaPago = obtenerClaveFecha(pago.fecha_pago);
    const cumpleFechaDesde = !fechaDesde || (claveFechaPago !== null && claveFechaPago >= fechaDesde);
    const cumpleFechaHasta = !fechaHasta || (claveFechaPago !== null && claveFechaPago <= fechaHasta);
    const cumpleBusqueda = busqueda === '' || 
      pago.usuario?.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      pago.usuario?.email.toLowerCase().includes(busqueda.toLowerCase()) ||
      pago.concepto.toLowerCase().includes(busqueda.toLowerCase());
    
    return (
      cumpleFiltroEstado &&
      cumpleFiltroMetodo &&
      cumpleFechaDesde &&
      cumpleFechaHasta &&
      cumpleBusqueda
    );
  });

  // Calcular estadísticas
  const totalIngresos = pagos
    .filter(p => p.estado === 'completado')
    .reduce((sum, p) => sum + Number(p.monto), 0);
  
  const pagosPendientes = pagos.filter(p => p.estado === 'pendiente').length;
  
  const pagosCompletados = pagos.filter(p => p.estado === 'completado').length;

  // Calcular ingresos del mes actual
  const mesActual = new Date().getMonth();
  const anioActual = new Date().getFullYear();
  const ingresosMesActual = pagos
    .filter(p => {
      // Postgres `date` arrives as "YYYY-MM-DD". JS parses that as UTC and can show the previous day in CL timezone.
      const fechaPago = /^\d{4}-\d{2}-\d{2}$/.test(p.fecha_pago)
        ? new Date(`${p.fecha_pago}T00:00:00`)
        : new Date(p.fecha_pago);
      return fechaPago.getMonth() === mesActual && 
             fechaPago.getFullYear() === anioActual &&
             p.estado === 'completado';
    })
    .reduce((sum, p) => sum + Number(p.monto), 0);

  const formatearMonto = (monto: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(monto);
  };

  const formatearFecha = (fecha: string) => {
    // Postgres `date` arrives as "YYYY-MM-DD". JS parses that as UTC and can show the previous day in CL timezone.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? new Date(`${fecha}T00:00:00`) : new Date(fecha);
    return d.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'bg-green-100 text-green-800';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'rechazado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const obtenerIconoMetodo = (metodo: string) => {
    switch (normalizarMetodoPago(metodo)) {
      case 'efectivo':
        return 'ri-money-dollar-circle-line';
      case 'tarjeta':
        return 'ri-bank-card-line';
      case 'transferencia':
        return 'ri-exchange-line';
      case 'cheque':
        return 'ri-file-list-3-line';
      case 'deposito':
        return 'ri-bank-line';
      default:
        return 'ri-wallet-line';
    }
  };

  const obtenerNombreMetodo = (metodo: string) => {
    switch (normalizarMetodoPago(metodo)) {
      case 'efectivo':
        return 'Efectivo';
      case 'tarjeta':
        return 'Tarjeta';
      case 'transferencia':
        return 'Transferencia';
      case 'cheque':
        return 'Cheque';
      case 'deposito':
        return 'Deposito';
      case 'otro':
        return 'Otro';
      default:
        return metodo || 'Metodo no definido';
    }
  };

  const handlePagoRegistrado = () => {
    cargarPagos();
    setMostrarModalRegistrar(false);
  };

  const descargarPDF = (pago: Pago) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header con fondo teal
    doc.setFillColor(13, 148, 136);
    doc.rect(0, 0, pageWidth, 48, 'F');

    // Título
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Comprobante de Pago', 20, 24);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`ID: ${pago.id.slice(0, 8).toUpperCase()}`, 20, 34);
    doc.text(`Fecha de emisión: ${new Date().toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' })}`, 20, 42);

    // Estado en la esquina derecha
    const estadoTexto = pago.estado.charAt(0).toUpperCase() + pago.estado.slice(1);
    if (pago.estado === 'completado') {
      doc.setFillColor(220, 252, 231);
      doc.setTextColor(21, 128, 61);
    } else if (pago.estado === 'pendiente') {
      doc.setFillColor(254, 249, 195);
      doc.setTextColor(161, 98, 7);
    } else {
      doc.setFillColor(254, 226, 226);
      doc.setTextColor(185, 28, 28);
    }
    const estadoWidth = doc.getTextWidth(estadoTexto) + 16;
    doc.roundedRect(pageWidth - 20 - estadoWidth, 20, estadoWidth, 10, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(estadoTexto, pageWidth - 20 - estadoWidth + 8, 27);

    // Línea separadora
    let y = 60;

    // Monto destacado
    doc.setTextColor(17, 24, 39);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Monto Total', 20, y);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(13, 148, 136);
    doc.text(formatearMonto(Number(pago.monto)), 20, y + 14);

    y += 30;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.line(20, y, pageWidth - 20, y);
    y += 12;

    // Datos del cliente
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(107, 114, 128);
    doc.text('DATOS DEL CLIENTE', 20, y);
    y += 10;

    doc.setTextColor(17, 24, 39);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const datoCliente = [
      ['Nombre', pago.usuario?.nombre || 'No disponible'],
      ['Email', pago.usuario?.email || 'No disponible'],
      ['Teléfono', pago.usuario?.telefono || 'No disponible'],
      ['Membresía', pago.usuario?.tipo_membresia || 'No disponible'],
    ];

    datoCliente.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text(`${label}:`, 20, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(17, 24, 39);
      doc.text(value, 75, y);
      y += 8;
    });

    y += 6;
    doc.setDrawColor(229, 231, 235);
    doc.line(20, y, pageWidth - 20, y);
    y += 12;

    // Detalles del pago
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(107, 114, 128);
    doc.text('DETALLES DEL PAGO', 20, y);
    y += 10;

    const obtenerNombreMetodo = (metodo: string) => {
      switch (metodo) {
        case 'efectivo': return 'Efectivo';
        case 'tarjeta': return 'Tarjeta';
        case 'transferencia': return 'Transferencia Bancaria';
        case 'cheque': return 'Cheque';
        case 'deposito': return 'Depósito';
        default: return metodo;
      }
    };

    const detallesPago: [string, string][] = [
      ['Concepto', pago.concepto || 'No especificado'],
      ['Método de Pago', obtenerNombreMetodo(pago.metodo_pago)],
      ['Fecha de Pago', formatearFecha(pago.fecha_pago)],
      ['Fecha de Registro', new Date(pago.created_at).toLocaleString('es-CL', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })],
    ];

    if (pago.banco) {
      detallesPago.push(['Banco', pago.banco]);
    }
    if (pago.numero_comprobante) {
      detallesPago.push(['N° Comprobante', pago.numero_comprobante]);
    }

    detallesPago.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(10);
      doc.text(`${label}:`, 20, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(17, 24, 39);
      doc.text(value, 75, y);
      y += 8;
    });

    // Notas
    if (pago.notas) {
      y += 6;
      doc.setDrawColor(229, 231, 235);
      doc.line(20, y, pageWidth - 20, y);
      y += 12;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text('NOTAS', 20, y);
      y += 10;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(10);
      const notasLines = doc.splitTextToSize(pago.notas, pageWidth - 40);
      doc.text(notasLines, 20, y);
    }

    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 20;
    doc.setDrawColor(229, 231, 235);
    doc.line(20, footerY - 8, pageWidth - 20, footerY - 8);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.setFont('helvetica', 'normal');
    doc.text('Este documento es un comprobante de pago generado automáticamente.', pageWidth / 2, footerY, { align: 'center' });

    // Descargar
    const nombreArchivo = `comprobante_pago_${pago.id.slice(0, 8)}_${pago.fecha_pago}.pdf`;
    doc.save(nombreArchivo);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
            <p className="mt-4 text-gray-600">Cargando pagos...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Encabezado */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pagos</h1>
            <p className="text-gray-600 mt-1">Gestiona los pagos y transacciones</p>
          </div>
          <button
            onClick={handleOpenRegistrarModal}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line text-xl"></i>
            Registrar Pago
          </button>
        </div>

        {/* Tarjetas de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Ingresos</p>
                <p className="text-2xl font-bold text-gray-900">{formatearMonto(totalIngresos)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Mes Actual</p>
                <p className="text-2xl font-bold text-gray-900">{formatearMonto(ingresosMesActual)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                <p className="text-2xl font-bold text-gray-900">{pagosPendientes}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Completados</p>
                <p className="text-2xl font-bold text-gray-900">{pagosCompletados}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-2xl text-teal-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Buscar por usuario, email o concepto..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="todos">Todos los estados</option>
              <option value="completado">Completado</option>
              <option value="pendiente">Pendiente</option>
              <option value="rechazado">Rechazado</option>
            </select>

            <select
              value={filtroMetodo}
              onChange={(e) => setFiltroMetodo(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="todos">Todos los métodos</option>
                            {metodosPagoManual.map((metodo) => (
                <option key={metodo.value} value={metodo.value}>
                  {metodo.label}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              title="Fecha desde"
            />

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                title="Fecha hasta"
              />
              {(fechaDesde || fechaHasta) && (
                <button
                  type="button"
                  onClick={() => {
                    setFechaDesde('');
                    setFechaHasta('');
                  }}
                  className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Limpiar rango de fechas"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>

            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
              <i className="ri-download-line"></i>
              Exportar
            </button>
          </div>
        </div>

        {/* Tabla de pagos */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Concepto</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Monto</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Método</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-file-list-line text-4xl mb-2"></i>
                      <p>No se encontraron pagos</p>
                    </td>
                  </tr>
                ) : (
                  pagosFiltrados.map((pago) => (
                    <tr key={pago.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-semibold text-teal-700">
                              {pago.usuario?.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{pago.usuario?.nombre || 'Usuario desconocido'}</p>
                            <p className="text-sm text-gray-500">{pago.usuario?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{pago.concepto}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900">{formatearMonto(Number(pago.monto))}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <i className={`${obtenerIconoMetodo(pago.metodo_pago)} text-lg text-gray-600`}></i>
                          <span className="text-sm text-gray-900">{obtenerNombreMetodo(pago.metodo_pago)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{formatearFecha(pago.fecha_pago)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${obtenerColorEstado(pago.estado)}`}>
                          {pago.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPagoSeleccionado(pago)}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-eye-line text-lg"></i>
                          </button>
                          <button
                            onClick={() => descargarPDF(pago)}
                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-download-line text-lg"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Registrar Pago */}
        {mostrarModalRegistrar && (
          <RegistrarPagoModal
            onClose={() => setMostrarModalRegistrar(false)}
            onPagoRegistrado={handlePagoRegistrado}
            metodosPagoDisponibles={metodosPagoManual}
          />
        )}

        {/* Modal Detalle Pago */}
        {pagoSeleccionado && (
          <DetallePagoModal
            pago={pagoSeleccionado}
            onClose={() => setPagoSeleccionado(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}




